/**
 * Report chart colours come from the theme, not from PHP.
 *
 * Filament already themes a chart, but only at chart level: `applyChartColors()` reads four
 * probe spans and writes `options.backgroundColor` / `borderColor` / `color`, then re-runs on
 * every theme change. Chart.js resolves DATASET colour over chart colour, so the moment a widget
 * names a colour per dataset it opts out of all of that — the value is frozen at render time,
 * and PHP has no way to know whether the reader is in light or dark mode.
 *
 * So a widget names a ROLE instead: `nx:success`, `nx:series-3`, `nx:muted`. This plugin
 * resolves the role against `--nx-chart-<role>` on every update, and because Filament's
 * `updateChartTheme()` ends in `chart.update('none')`, a theme toggle re-enters here.
 *
 * Three details the implementation depends on:
 *
 *   - Tokens are stashed on the DATASET OBJECT, not on the chart. A filter change assigns a
 *     whole new `chart.data`, and the feedback charts go from one no-data dataset to three
 *     semantic ones — a per-chart snapshot would map stale colours onto datasets that changed
 *     count and order.
 *
 *   - Resolved values are rasterised to `rgb()`. Filament v4 stores every registered colour as
 *     `oklch()`, and Chart.js' colour helper (@kurkle/color) cannot parse oklch at all — so
 *     `darken()` silently returns its input and bar borders, hover borders and the doughnut
 *     legend's `strokeStyle` all collapse onto the fill. Painting the value into a 1x1 canvas
 *     and reading it back gives the same colour in a form Chart.js can manipulate.
 *
 *   - No `if (!chart.width) return` guard, unlike the density plugin next door. That one has
 *     nothing to do until the container has been measured; this one must resolve before the very
 *     first paint, or the first frame is painted with the literal string "nx:series-1".
 */

const TOKEN_PREFIX = 'nx:';

// keys Chart.js and Filament's `normalizeDatasets()` may carry a colour on
const COLOR_KEYS = [
    'backgroundColor',
    'borderColor',
    'hoverBackgroundColor',
    'hoverBorderColor',
    'pointBackgroundColor',
    'pointBorderColor',
    'pointHoverBackgroundColor',
    'pointHoverBorderColor',
];

const rasterCanvas = () => {
    if (!rasterCanvas.el) {
        rasterCanvas.el = document.createElement('canvas');
        rasterCanvas.el.width = 1;
        rasterCanvas.el.height = 1;
    }

    return rasterCanvas.el;
};

/**
 * Any CSS colour -> "rgb(r, g, b)" / "rgba(r, g, b, a)". The canvas is the only
 * thing in the page guaranteed to understand every colour syntax the theme might
 * use, and it hands back a form @kurkle/color can parse.
 */
const toRgb = (value, alpha = 1) => {
    const canvas = rasterCanvas();
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    ctx.clearRect(0, 0, 1, 1);
    ctx.fillStyle = '#000';
    ctx.fillStyle = value;

    // an unparseable value leaves fillStyle at the previous colour, so paint and
    // read back rather than trusting the assignment
    ctx.fillRect(0, 0, 1, 1);

    const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
    const opacity = (a / 255) * alpha;

    return opacity >= 1 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${opacity.toFixed(3)})`;
};

const resolveToken = (token, element, cache) => {
    if (cache.has(token)) {
        return cache.get(token);
    }

    // `nx:success/10` — the area fill under a line is the same colour as the
    // line, at a tenth of it. Same `/<percent>` spelling Filament and Tailwind
    // use, so it reads as an opacity rather than as a different role.
    const [role, opacity] = token.slice(TOKEN_PREFIX.length).split('/');
    const name = `--nx-chart-${role}`;
    const raw = getComputedStyle(element).getPropertyValue(name).trim();

    // an unknown role resolves to nothing, and the cleanup pass below strips the
    // key entirely — so the dataset falls back to Filament's themed chart-level
    // colour rather than painting a literal "nx:whatever"
    const resolved = raw ? toRgb(raw, opacity === undefined ? 1 : Number(opacity) / 100) : undefined;
    cache.set(token, resolved);

    return resolved;
};

const isToken = value => typeof value === 'string' && value.startsWith(TOKEN_PREFIX);

/**
 * The roles a dataset was authored with, captured before this plugin overwrote
 * them. Kept on the dataset object so it travels with `chart.data` replacement.
 */
const authoredTokens = dataset => {
    if (dataset.$nxTokens) {
        return dataset.$nxTokens;
    }

    const tokens = {};

    for (const key of COLOR_KEYS) {
        const value = dataset[key];

        if (isToken(value)) {
            tokens[key] = value;
        } else if (Array.isArray(value) && value.some(isToken)) {
            tokens[key] = value.slice();
        }
    }

    // Filament's normalizeDatasets() copies borderColor onto pointBackgroundColor
    // for a line chart but leaves pointBorderColor alone, so Chart.js rings every
    // point with the chart-level brand colour: measured as 324px of primary-500
    // scattered along the green series on the feedback trend. Adopt the role here,
    // with the other authored ones, rather than deriving it after the fact — by
    // the time the resolved values have been written Chart.js has already read
    // the dataset scope for this pass.
    if (tokens.borderColor && !Array.isArray(tokens.borderColor) && dataset.pointBorderColor === undefined) {
        tokens.pointBorderColor = tokens.borderColor;
    }

    // Filament derives pointBackgroundColor from borderColor in initChart(), so
    // it is already a token by the time this runs and gets captured above. If it
    // is NOT there, the dataset never had a border role to derive from.
    if (tokens.borderColor && !Array.isArray(tokens.borderColor)) {
        tokens.pointBackgroundColor ??= tokens.borderColor;
    }

    Object.defineProperty(dataset, '$nxTokens', { value: tokens, enumerable: false });

    return tokens;
};

const applyTokens = (dataset, element, cache) => {
    const tokens = authoredTokens(dataset);

    for (const [key, authored] of Object.entries(tokens)) {
        if (Array.isArray(authored)) {
            dataset[key] = authored.map(entry =>
                isToken(entry) ? (resolveToken(entry, element, cache) ?? entry) : entry
            );

            continue;
        }

        dataset[key] = resolveToken(authored, element, cache) ?? authored;
    }

    return Object.keys(tokens).length > 0;
};

export default {
    id: 'nexlabChartPalette',

    beforeUpdate(chart) {
        const element = chart.canvas ?? document.documentElement;

        // one read per role per pass: getComputedStyle is the expensive part, and
        // a doughnut can carry the same role across a dozen slices
        const cache = new Map();
        let touched = false;

        for (const dataset of chart.data?.datasets ?? []) {
            touched = applyTokens(dataset, element, cache) || touched;
        }

        if (!touched) {
            return;
        }

        // Filament copies backgroundColor into borderColor/hoverBorderColor via
        // darken() at init, before any of this ran, so those keys can hold a
        // token that was never authored as one. Anything still unresolved after
        // the pass above is that copy — drop it and let Chart.js re-derive.
        for (const dataset of chart.data?.datasets ?? []) {
            for (const key of COLOR_KEYS) {
                if (isToken(dataset[key])) {
                    delete dataset[key];
                } else if (Array.isArray(dataset[key]) && dataset[key].some(isToken)) {
                    dataset[key] = dataset[key].filter(entry => !isToken(entry));
                }
            }
        }
    },

    /**
     * Value labels are drawn by chartjs-plugin-datalabels, which resolves its
     * colour as `resolve([config.color, Chart.defaults.color])` — the global
     * default, never the per-chart `options.color` Filament themes. Left alone
     * they land on Chart.js' static #666 in both schemes. Filament has already
     * written the themed text colour onto `options.color` by the time this runs.
     */
    beforeDatasetsUpdate(chart) {
        const themed = chart.options?.color;

        if (typeof themed !== 'string') {
            return;
        }

        chart.options.plugins ??= {};
        chart.options.plugins.datalabels ??= {};

        // capture the widget's own choice once. `??=` on the live value would
        // read back whatever the previous pass wrote, so the label colour would
        // be pinned to the scheme the chart first mounted in and never follow a
        // theme toggle — the exact bug this plugin exists to avoid
        if (chart.$nxAuthoredLabelColor === undefined) {
            chart.$nxAuthoredLabelColor = chart.options.plugins.datalabels.color ?? null;
        }

        if (chart.$nxAuthoredLabelColor === null) {
            chart.options.plugins.datalabels.color = themed;
        }
    },
};
